import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { usePropertyPanel } from '../PropertyPanelContext';
import { PaddingControl, SkinSelector } from '../CommonProperties'; // PaddingControl과 SkinSelector 컴포넌트 import

// 토글 버튼 컴포넌트
const ToggleButton = ({ label, checked, onChange, fullWidth = false }) => {
  return (
    <div className="property-row" style={{ marginBottom: '10px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        width: '100%'
      }}>
        <span style={{ minWidth: fullWidth ? '50%' : '30%' }}>{label}</span>
        <div 
          style={{ 
            position: 'relative', 
            width: '44px', 
            height: '22px', 
            backgroundColor: checked ? '#3b82f6' : '#ccc',
            borderRadius: '34px', 
            transition: '.3s',
            cursor: 'pointer'
          }}
          onClick={() => onChange(!checked)}
        >
          <div 
            style={{ 
              position: 'absolute',
              height: '18px',
              width: '18px',
              left: checked ? '24px' : '2px',
              bottom: '2px',
              backgroundColor: 'white',
              transition: '.3s',
              borderRadius: '50%'
            }}
          />
        </div>
      </div>
    </div>
  );
};

const SignupProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // PropertyPanel 컨텍스트에서 공통 함수 및 상태 가져오기
  const context = usePropertyPanel() || {};
  
  // varFields 속성 변경 핸들러 - 최상위 varFields 속성을 직접 업데이트
  const handleVarFieldsChange = (varName, field, value) => {
    if (!selectedComponent) return;
    
    // 기존 varFields 값 가져오기 (깊은 복사)
    const existingVarFields = JSON.parse(JSON.stringify(selectedComponent.varFields || {}));
    
    // 지정된 필드가 없는 경우 기본값으로 초기화
    if (!existingVarFields[varName]) {
      existingVarFields[varName] = { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      };
    }
    
    // 필드 값 업데이트
    existingVarFields[varName][field] = value;
    
    // Redux 상태 업데이트 (최상위 varFields 속성으로 직접 업데이트)
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        varFields: existingVarFields
      }
    }));
  };
  
  // 컴포넌트 속성 변경 핸들러
  const handleComponentPropsChange = (property, value) => {
    if (!selectedComponent) return;
    
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    existingProps[property] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };
  
  // 스타일 변경 핸들러
  const handleStyleChange = (property, value) => {
    if (!selectedComponent) return;
    
    const existingStyle = JSON.parse(JSON.stringify(selectedComponent.style || {}));
    existingStyle[property] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        style: existingStyle
      }
    }));
  };
  
  // 현재 varFields 값 가져오기 (componentProps 내부가 아닌 최상위 속성에서)
  const getVarFieldValue = (varName, field, defaultValue) => {
    try {
      return selectedComponent?.varFields?.[varName]?.[field] ?? defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };
  
  // 기본 회원정보 필드 표시 여부 가져오기
  const getBasicFieldValue = (fieldName, defaultValue = true) => {
    try {
      return selectedComponent?.basicFields?.[fieldName] ?? defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };
  
  // 기본 회원정보 필드 표시 여부 변경 핸들러
  const handleBasicFieldChange = (fieldName, value) => {
    if (!selectedComponent) return;
    
    // 기존 basicFields 값 가져오기 (깊은 복사)
    const existingBasicFields = JSON.parse(JSON.stringify(selectedComponent.basicFields || {
      userId: true,
      password: true,
      name: true,
      phone: true,
      email: true,
      birthday: true,
      address: true,
      referralCode: true
    }));
    
    // 필드 값 업데이트
    existingBasicFields[fieldName] = value;
    
    // Redux 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        basicFields: existingBasicFields
      }
    }));
  };
  
  // 필드 설정 렌더링 - var01, var02, var03 같은 변수 필드에 대한 처리
  const renderVarField = (varName, title) => {
    return (
      <>
        <div className="property-group-subtitle">
          {title} ({varName})
        </div>
        
        <ToggleButton
          label="표시 여부"
          checked={getVarFieldValue(varName, 'show', false)}
          onChange={(value) => handleVarFieldsChange(varName, 'show', value)}
        />
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>필드 이름</label>
          <input 
            type="text" 
            value={getVarFieldValue(varName, 'label', '')}
            onChange={(e) => handleVarFieldsChange(varName, 'label', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>필드 타입</label>
          <select 
            value={getVarFieldValue(varName, 'type', 'input')}
            onChange={(e) => handleVarFieldsChange(varName, 'type', e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="input">텍스트 입력</option>
            <option value="select">선택 상자</option>
            <option value="radio">라디오 버튼</option>
            <option value="document">동의 문서</option>
            <option value="account">계좌번호</option>
            <option value="ssn">주민등록번호</option>
          </select>
        </div>
        
        <ToggleButton
          label="필수 입력"
          checked={getVarFieldValue(varName, 'required', false)}
          onChange={(value) => handleVarFieldsChange(varName, 'required', value)}
        />
        
        {/* 선택상자 또는 라디오 버튼 타입인 경우 옵션 관리 UI 표시 */}
        {(getVarFieldValue(varName, 'type', '') === 'select' || 
          getVarFieldValue(varName, 'type', '') === 'radio') && (
          <div className="property-row">
            <label style={{ minWidth: '30%', display: 'block', marginBottom: '8px' }}>
              옵션 목록
            </label>
            <div style={{ width: '100%' }}>
              {(getVarFieldValue(varName, 'options', []) || []).map((option, index) => (
                <div key={index} style={{ display: 'flex', marginBottom: '6px', gap: '4px' }}>
                  <input 
                    type="text" 
                    placeholder="라벨"
                    value={option.label || ''}
                    onChange={(e) => {
                      const newOptions = [...(getVarFieldValue(varName, 'options', []) || [])];
                      const newLabel = e.target.value;
                      
                      // 라벨 변경 시 값이 비어 있으면 라벨과 동일하게 설정
                      const newValue = option.value || newLabel;
                      
                      newOptions[index] = { 
                        ...newOptions[index], 
                        label: newLabel,
                        value: newValue
                      };
                      handleVarFieldsChange(varName, 'options', newOptions);
                    }}
                    style={{ flex: 2 }}
                  />
                  <input 
                    type="text" 
                    placeholder="값"
                    value={option.value || ''}
                    onChange={(e) => {
                      const newOptions = [...(getVarFieldValue(varName, 'options', []) || [])];
                      newOptions[index] = { ...newOptions[index], value: e.target.value };
                      handleVarFieldsChange(varName, 'options', newOptions);
                    }}
                    style={{ flex: 1 }}
                  />
                  <button 
                    onClick={() => {
                      const newOptions = [...(getVarFieldValue(varName, 'options', []) || [])];
                      newOptions.splice(index, 1);
                      handleVarFieldsChange(varName, 'options', newOptions);
                    }}
                    style={{ 
                      padding: '0 6px', 
                      backgroundColor: '#ff5252', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer', 
                      fontSize: '12px' 
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const defaultLabel = '옵션 ' + ((getVarFieldValue(varName, 'options', []) || []).length + 1);
                  const newOptions = [...(getVarFieldValue(varName, 'options', []) || []), { 
                    label: defaultLabel, 
                    value: defaultLabel 
                  }];
                  handleVarFieldsChange(varName, 'options', newOptions);
                }}
                style={{ 
                  padding: '4px 8px', 
                  backgroundColor: '#4a90e2', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer', 
                  width: '100%', 
                  marginTop: '6px', 
                  fontSize: '12px' 
                }}
              >
                옵션 추가
              </button>
            </div>
          </div>
        )}
        
        {/* 추가적인 필드 타입별 설정 */}
        {getVarFieldValue(varName, 'type', '') === 'document' && (
          <div className="property-row">
            <label style={{ minWidth: '30%', display: 'block', marginBottom: '8px' }}>
              동의 문서 내용
            </label>
            <div style={{ width: '100%' }}>
              <textarea 
                value={getVarFieldValue(varName, 'content', '')}
                onChange={(e) => handleVarFieldsChange(varName, 'content', e.target.value)}
                placeholder="동의 문서 내용을 입력해주세요."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  };
  
  return (
    <>
      {/* 공통 속성인 PaddingControl 사용 */}
      {/* <PaddingControl /> */}
      
      {/* 스킨 선택 UI - 공통 컴포넌트 사용 */}
      <SkinSelector 
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => handleComponentPropsChange('skin', skinId)}
      />
      
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          회원정보 필드 설정
        </div>

        {/* 기본 회원정보 필드 표시 여부 설정 */}
        <ToggleButton
          label="아이디 표시"
          checked={getBasicFieldValue('userId', true)}
          onChange={(value) => handleBasicFieldChange('userId', value)}
        />

        <ToggleButton
          label="비밀번호 표시"
          checked={getBasicFieldValue('password', true)}
          onChange={(value) => handleBasicFieldChange('password', value)}
        />

        <ToggleButton
          label="이름 표시"
          checked={getBasicFieldValue('name', true)}
          onChange={(value) => handleBasicFieldChange('name', value)}
        />

        <ToggleButton
          label="핸드폰 번호 표시"
          checked={getBasicFieldValue('phone', true)}
          onChange={(value) => handleBasicFieldChange('phone', value)}
        />

        <ToggleButton
          label="이메일 표시"
          checked={getBasicFieldValue('email', true)}
          onChange={(value) => handleBasicFieldChange('email', value)}
        />

        <ToggleButton
          label="생년월일 표시"
          checked={getBasicFieldValue('birthday', true)}
          onChange={(value) => handleBasicFieldChange('birthday', value)}
        />

        <ToggleButton
          label="주소 표시"
          checked={getBasicFieldValue('address', true)}
          onChange={(value) => handleBasicFieldChange('address', value)}
        />

        <ToggleButton
          label="추천인 코드 표시"
          checked={getBasicFieldValue('referralCode', true)}
          onChange={(value) => handleBasicFieldChange('referralCode', value)}
        />
      </div>

      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          커스텀 필드 설정
        </div>
        
        {renderVarField('var01', '추가 필드 1')}
        {renderVarField('var02', '추가 필드 2')}
        {renderVarField('var03', '추가 필드 3')}
        {renderVarField('var04', '추가 필드 4')}
        {renderVarField('var05', '추가 필드 5')}
        {renderVarField('var06', '추가 필드 6')}
        {renderVarField('var07', '추가 필드 7')}
        {renderVarField('var08', '추가 필드 8')}
        {renderVarField('var09', '추가 필드 9')}
        {renderVarField('var10', '추가 필드 10')}
      </div>
      
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          스타일 설정
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>패딩</label>
          <input 
            type="text" 
            value={selectedComponent?.style?.padding || '20px'}
            onChange={(e) => handleStyleChange('padding', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>배경색</label>
          <input 
            type="color" 
            value={selectedComponent?.style?.backgroundColor || '#ffffff'}
            onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="property-row">
          <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
          <input 
            type="text" 
            value={selectedComponent?.style?.borderRadius || '8px'}
            onChange={(e) => handleStyleChange('borderRadius', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </>
  );
};

export default SignupProperties;